import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  FileSpreadsheet,
  FileText,
  Check,
  Loader2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  STUDENT_EXPORT_COLUMNS,
  STAFF_EXPORT_COLUMNS,
  PROFESSOR_EXPORT_COLUMNS,
  PARENT_EXPORT_COLUMNS,
  type ExportColumn,
  type StaffUser,
  type ProfessorUser,
  type StudentUser,
  type ParentUser,
} from '@/services/superAdminUserService';

type UserType = 'staff' | 'professors' | 'students' | 'parents';

interface UserExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: UserType;
  institutionId: string;
  institutionName: string;
  academicYearId?: string;
  currentData: (StaffUser | ProfessorUser | StudentUser | ParentUser)[];
}

const CATEGORY_LABELS: Record<string, string> = {
  basic: 'Informations de base',
  identity: 'Identite',
  academic: 'Academique',
  contact: 'Contact',
  professional: 'Professionnel',
  status: 'Statut & Aides',
  links: 'Liens',
  financial: 'Informations financieres',
  emergency: 'Contact d\'urgence',
};

export const UserExportModal: React.FC<UserExportModalProps> = ({
  isOpen,
  onClose,
  userType,
  institutionId: _institutionId,
  institutionName,
  academicYearId: _academicYearId,
  currentData,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [exportFormat, setExportFormat] = useState<'excel' | 'pdf'>('excel');
  const [isExporting, setIsExporting] = useState(false);

  // Get columns based on user type
  const columns = useMemo(() => {
    switch (userType) {
      case 'staff':
        return STAFF_EXPORT_COLUMNS;
      case 'professors':
        return PROFESSOR_EXPORT_COLUMNS;
      case 'students':
        return STUDENT_EXPORT_COLUMNS;
      case 'parents':
        return PARENT_EXPORT_COLUMNS;
      default:
        return [];
    }
  }, [userType]);

  // Initialize selected columns with defaults
  React.useEffect(() => {
    const defaults = new Set(
      columns.filter((c) => c.defaultChecked).map((c) => c.key)
    );
    // Always include basic columns
    columns.filter(c => c.category === 'basic').forEach(c => defaults.add(c.key));
    setSelectedColumns(defaults);
  }, [columns]);

  // Group columns by category
  const columnsByCategory = useMemo(() => {
    const grouped: Record<string, ExportColumn[]> = {};
    columns.forEach((col) => {
      if (!grouped[col.category]) {
        grouped[col.category] = [];
      }
      grouped[col.category].push(col);
    });
    return grouped;
  }, [columns]);

  const toggleColumn = (key: string) => {
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    const categoryColumns = columnsByCategory[category] || [];
    const allSelected = categoryColumns.every((c) => selectedColumns.has(c.key));

    setSelectedColumns((prev) => {
      const next = new Set(prev);
      categoryColumns.forEach((c) => {
        if (allSelected) {
          next.delete(c.key);
        } else {
          next.add(c.key);
        }
      });
      return next;
    });
  };

  const handleExport = async () => {
    if (selectedColumns.size === 0) return;
    setIsExporting(true);

    try {
      // Prepare data for export
      const exportData = currentData.map((item) => {
        const row: Record<string, any> = {};

        // Always add institution and campus
        row['Etablissement'] = institutionName;

        // Add selected columns
        selectedColumns.forEach((key) => {
          const col = columns.find((c) => c.key === key);
          if (!col) return;

          let value: any;

          // Handle different user types
          if (userType === 'students') {
            const student = item as StudentUser & { [key: string]: any };
            switch (key) {
              case 'firstName':
                value = student.firstName;
                break;
              case 'lastName':
                value = student.lastName;
                break;
              case 'phone':
                value = student.phone;
                break;
              case 'email':
                value = student.email;
                break;
              case 'studentCode':
                value = student.studentCode;
                break;
              case 'dateOfBirth':
                value = student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString('fr-FR') : '';
                break;
              case 'gender':
                value = student.gender === 'M' ? 'Masculin' : student.gender === 'F' ? 'Feminin' : student.gender;
                break;
              case 'paysOrigine':
                value = student.nationality || student.paysOrigine;
                break;
              case 'cnie':
                value = student.cnie;
                break;
              case 'passport':
                value = student.passportNumber || student.passport;
                break;
              case 'programName':
                value = student.program?.name;
                break;
              case 'niveauFormation':
                value = student.niveauFormation;
                break;
              case 'diplomeInscrit':
                value = student.diplomeInscrit;
                break;
              case 'codeFiliereInscrit':
                value = student.codeFiliereInscrit;
                break;
              case 'situation':
                const situationLabels: Record<string, string> = { N: 'Nouveau', R: 'Redoublant', P: 'Promu', D: 'Diplome' };
                value = situationLabels[student.situation] || student.situation;
                break;
              case 'numeroMassarCNE':
                value = student.numeroMassarCNE;
                break;
              case 'typeFormation':
                value = student.typeFormation === 'INITIALE' ? 'Initiale' : student.typeFormation === 'CONTINUE' ? 'Continue' : student.typeFormation;
                break;
              case 'premiereAnneeInscription':
                value = student.premiereAnneeInscription;
                break;
              case 'serieBaccalaureat':
                value = student.serieBaccalaureat;
                break;
              case 'anneeObtentionBac':
                value = student.anneeObtentionBac;
                break;
              case 'diplomeAcces':
                value = student.diplomeAcces;
                break;
              case 'specialiteDiplomeAcces':
                value = student.specialiteDiplomeAcces;
                break;
              case 'mentionDiplomeAcces':
                const mentionLabels: Record<string, string> = { p: 'Passable', ab: 'Assez Bien', b: 'Bien', tb: 'Tres Bien', e: 'Excellent', ns: 'Non Specifie' };
                value = mentionLabels[student.mentionDiplomeAcces] || student.mentionDiplomeAcces;
                break;
              case 'address':
                value = student.address;
                break;
              case 'resident':
                value = student.resident === 'O' ? 'Oui' : student.resident === 'N' ? 'Non' : student.resident;
                break;
              case 'fonctionnaire':
                value = student.fonctionnaire === 'O' ? 'Oui' : student.fonctionnaire === 'N' ? 'Non' : student.fonctionnaire;
                break;
              case 'pourcentageBourse':
                value = student.pourcentageBourse != null ? `${student.pourcentageBourse}%` : '';
                break;
              case 'mobilite':
                value = student.mobilite === 'O' ? 'Oui' : student.mobilite === 'N' ? 'Non' : student.mobilite;
                break;
              case 'enrollmentDate':
                value = student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString('fr-FR') : '';
                break;
              case 'urgenceNom':
                value = student.emergencyContactName || student.urgenceNom;
                break;
              case 'urgenceRelation':
                value = student.emergencyContactRelation || student.urgenceRelation;
                break;
              case 'urgenceNumero':
                value = student.emergencyContactPhone || student.urgenceNumero;
                break;
              default:
                value = student[key];
            }
            // Add campus for students
            row['Campus'] = student.campus?.name || '';
          } else if (userType === 'parents') {
            const parent = item as ParentUser;
            switch (key) {
              case 'firstName':
                value = parent.firstName;
                break;
              case 'lastName':
                value = parent.lastName;
                break;
              case 'phone':
                value = parent.phone;
                break;
              case 'email':
                value = parent.email;
                break;
              case 'linkedStudents':
                value = parent.linkedStudents?.map((s) => `${s.firstName} ${s.lastName}`).join(', ');
                break;
              case 'status':
                value = parent.status;
                break;
              default:
                value = (parent as any)[key];
            }
            // Add campus from first linked student
            row['Campus'] = parent.linkedStudents?.[0]?.campus?.name || '';
          } else {
            // Staff or Professors
            const user = item as (StaffUser | ProfessorUser) & { profile?: any; [key: string]: any };
            switch (key) {
              case 'firstName':
                value = user.firstName;
                break;
              case 'lastName':
                value = user.lastName;
                break;
              case 'phone':
                value = user.phone;
                break;
              case 'email':
                value = user.email;
                break;
              case 'staffCode':
                value = user.staffCode;
                break;
              case 'role':
                const roleLabels: Record<string, string> = {
                  DIRECTOR_GENERAL: 'Directeur General',
                  BRANCH_MANAGER: 'Directeur de Campus',
                  PEDAGOGICAL_DIRECTOR: 'Directeur Pedagogique',
                  FINANCIAL_MANAGER: 'Directeur Financier',
                  MARKETING_MANAGER: 'Directeur Marketing',
                  COMMERCIAL_MANAGER: 'Directeur Commercial',
                  PROGRAM_HEAD: 'Chef de Filiere',
                  ACADEMIC_COORDINATOR: 'Coordinateur Academique',
                  TEACHER: 'Professeur',
                };
                value = roleLabels[user.role] || user.role;
                break;
              case 'cnie':
                value = user.profile?.cnie;
                break;
              case 'passport':
                value = user.profile?.passport;
                break;
              case 'sexe':
                const sexe = user.profile?.sexe;
                value = sexe === 'M' ? 'Masculin' : sexe === 'F' ? 'Feminin' : sexe;
                break;
              case 'dateNaissance':
                value = user.profile?.dateNaissance ? new Date(user.profile.dateNaissance).toLocaleDateString('fr-FR') : '';
                break;
              case 'paysOrigine':
                value = user.profile?.paysOrigine;
                break;
              case 'fonction':
                value = user.profile?.fonction;
                break;
              case 'dateRecrutement':
                value = user.profile?.dateRecrutement ? new Date(user.profile.dateRecrutement).toLocaleDateString('fr-FR') : '';
                break;
              case 'diplome':
                value = user.profile?.diplome?.labelFr;
                break;
              case 'adresse':
                value = user.profile?.adresse;
                break;
              case 'salaireMensuelNet':
                value = user.profile?.salaireMensuelNet ? `${user.profile.salaireMensuelNet} MAD` : '';
                break;
              case 'rib':
                value = user.profile?.rib;
                break;
              case 'urgenceNom':
                value = user.profile?.urgenceNom;
                break;
              case 'urgenceRelation':
                value = user.profile?.urgenceRelation;
                break;
              case 'urgenceNumero':
                value = user.profile?.urgenceNumero;
                break;
              case 'statutProfesseur':
                const statutProf = (user as ProfessorUser).statutProfesseur || user.profile?.statutProfesseur;
                value = statutProf === 'P' ? 'Permanent' : statutProf === 'V' ? 'Vacataire' : statutProf;
                break;
              case 'specialite':
                value = (user as ProfessorUser).specialite || user.profile?.specialite;
                break;
              case 'matieresEnseignees':
                value = user.profile?.matieresEnseignees;
                break;
              case 'grade':
                value = (user as ProfessorUser).grade?.labelFr || user.profile?.grade?.labelFr;
                break;
              case 'departement':
                value = (user as ProfessorUser).departementAttache?.labelFr || user.profile?.departementAttache?.labelFr;
                break;
              case 'tauxHoraire':
                value = user.profile?.tauxHoraire ? `${user.profile.tauxHoraire} MAD/h` : '';
                break;
              case 'estOriginaireEtablissementPublic':
                const estPublic = user.profile?.estOriginaireEtablissementPublic;
                value = estPublic === 'O' ? 'Oui' : estPublic === 'N' ? 'Non' : estPublic;
                break;
              case 'etablissementOrigine':
                value = user.profile?.etablissementOrigine;
                break;
              default:
                value = user[key];
            }
            // Add campus for staff/professors
            row['Campus'] = user.campuses?.map((c) => c.name).join(', ') || '';
          }

          row[col.label] = value || '';
        });

        return row;
      });

      if (exportFormat === 'excel') {
        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths
        const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
          wch: Math.max(key.length, 15),
        }));
        ws['!cols'] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Utilisateurs');

        // Generate filename
        const userTypeLabel = {
          staff: 'Personnel',
          professors: 'Professeurs',
          students: 'Etudiants',
          parents: 'Parents',
        }[userType];
        const filename = `${institutionName}_${userTypeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download
        XLSX.writeFile(wb, filename);
      } else {
        // PDF export - for now just alert that it's not implemented
        alert('Export PDF sera disponible prochainement');
      }

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      alert('Erreur lors de l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  const userTypeLabel = {
    staff: 'Personnel',
    professors: 'Professeurs',
    students: 'Etudiants',
    parents: 'Parents',
  }[userType];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Exporter {userTypeLabel}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {currentData.length} utilisateur{currentData.length > 1 ? 's' : ''} a exporter
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Format Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Format d'export</label>
            <div className="flex gap-3">
              <button
                onClick={() => setExportFormat('excel')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-colors ${
                  exportFormat === 'excel'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileSpreadsheet className="h-5 w-5" />
                <span>Excel (.xlsx)</span>
              </button>
              <button
                onClick={() => setExportFormat('pdf')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-colors ${
                  exportFormat === 'pdf'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span>PDF</span>
              </button>
            </div>
          </div>

          {/* Column Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Colonnes a inclure ({selectedColumns.size} selectionnees)
            </label>
            <p className="text-xs text-gray-500 mb-4">
              Les colonnes "Etablissement" et "Campus" sont toujours incluses.
            </p>

            <div className="space-y-4">
              {Object.entries(columnsByCategory).map(([category, cols]) => {
                const allSelected = cols.every((c) => selectedColumns.has(c.key));
                const someSelected = cols.some((c) => selectedColumns.has(c.key));

                return (
                  <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-medium text-gray-700">
                        {CATEGORY_LABELS[category] || category}
                      </span>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          allSelected
                            ? 'bg-indigo-600 border-indigo-600'
                            : someSelected
                            ? 'bg-indigo-100 border-indigo-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {(allSelected || someSelected) && (
                          <Check className={`h-3 w-3 ${allSelected ? 'text-white' : 'text-indigo-600'}`} />
                        )}
                      </div>
                    </button>
                    <div className="px-4 py-3 grid grid-cols-2 gap-2">
                      {cols.map((col) => (
                        <label
                          key={col.key}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedColumns.has(col.key)}
                            onChange={() => toggleColumn(col.key)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{col.label}</span>
                          {col.defaultChecked && (
                            <span className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                              Par defaut
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleExport}
            disabled={selectedColumns.size === 0 || isExporting}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Export en cours...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Exporter
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserExportModal;
